import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { pageDataService, ThemeType } from 'common';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { Platform } from 'mcsclient/models';
import { AuthResult } from 'nativeAppShim';
import { openDialogSourceQueryParamName } from 'RouteConstants';
import { creditMonitoringOnboardingPath, identityOnboardingPath } from 'Routes';
import { isMobilePlatform, setIsPreviousDialog, setNativeDialogArea, setWebViewRendered, useAppDispatch, useAppSelector } from 'store';

import { NativeDialogArea } from './webViewState';

const smallDialogAreas = [NativeDialogArea.RemoveIdentity, NativeDialogArea.RemoveSubscription];

// Dialogs that need to be open in full page for mobile apps
const mobileFullScreenDialogAreas = [
  NativeDialogArea.AddIdentity,
  NativeDialogArea.BulkAdd,
  NativeDialogArea.OnboardFamilyMember,
  NativeDialogArea.RecommendationDialog,
  NativeDialogArea.CreditAlertDialog,
  NativeDialogArea.CreditReportDetails,
];

function getDialogSize(dialogArea: NativeDialogArea, platform: Platform | undefined) {
  if (smallDialogAreas.includes(dialogArea)) {
    return 'small';
  }

  if (isMobilePlatform(platform) && mobileFullScreenDialogAreas.includes(dialogArea)) {
    return 'full';
  }

  return 'large';
}

export const useWebView = (): {
  isWebView: boolean;
  isWebViewReady: boolean;
  openNativeDialog: (nativeDialogArea: NativeDialogArea, url: string, onDialogOpen?: () => void) => void;
  closeNativeDialog: (portalStateChanged: boolean, reloadDialog?: boolean) => void;
  onWebViewRendered: (area: string) => void;
  isWebViewDialogClosing: boolean;
  webViewPlatform: Platform | undefined;
  supportsDarkMode: boolean | undefined;
  theme: ThemeType | undefined;
  openNativeNotificationSettings: () => void;
  supportsLocalAuth: boolean;
  authenticateUser: () => void;
  authResult: AuthResult | undefined;
} => {
  const dispatch = useAppDispatch();
  const isWebView = pageDataService.isWebView();
  const webViewState = useAppSelector((state) => state.webView);
  const localAppState = useAppSelector((state) => state.webView.nativeAppState);
  const isWebViewDialogClosing = useAppSelector((state) => state.webView.dialogClosing);
  const authResult = useAppSelector((state) => state.webView.authResult);
  const isWebViewReady = !!(isWebView && webViewState.initComplete && localAppState && window.nativeAppApi);
  const isWebViewRendered = isWebView && webViewState.isWebViewRendered;
  const webViewPlatform = localAppState?.platform;
  const theme = localAppState?.theme;
  const supportsDarkMode = !!localAppState?.supportsDarkMode;
  const supportsLocalAuth = !!localAppState?.supportsLocalAuth;

  const openNativeDialog = useCallback(
    (nativeDialogArea: NativeDialogArea, url: string, onDialogOpen?: () => void) => {
      if (isWebViewReady) {
        dispatch(setNativeDialogArea(nativeDialogArea));
        const dialogSize = getDialogSize(nativeDialogArea, webViewPlatform);
        window.nativeAppApi.openDialog(url, dialogSize);
        onDialogOpen && onDialogOpen();
      }
    },
    [dispatch, isWebViewReady, webViewPlatform]
  );

  const closeNativeDialog = useCallback(
    (portalStateChanged: boolean, reloadDialog = false) => {
      if (isWebViewReady) {
        dispatch(setIsPreviousDialog());
        if (reloadDialog) {
          window.location.reload();
        }
        window.nativeAppApi.closeDialog(portalStateChanged);
      }
    },
    [dispatch, isWebViewReady]
  );

  const onWebViewRendered = useCallback(
    (area: string) => {
      if (isWebViewReady && !isWebViewRendered) {
        try {
          dispatch(setWebViewRendered(true));
          window.nativeAppApi.onWebViewRendered();
          TelemetryProvider.Instance.trackEvent('onWebViewRendered', { area });
        } catch {
          // ignore proxy exception for back-compat
        }
      }
    },
    [dispatch, isWebViewReady, isWebViewRendered]
  );

  const openNativeNotificationSettings = useCallback(() => {
    window.nativeAppApi.openNotificationSettings();
  }, []);

  const authenticateUser = useCallback(() => {
    if (isWebViewReady) {
      window.nativeAppApi.authenticateUser();
      TelemetryProvider.Instance.trackEvent('authenticateUser');
    }
  }, [isWebViewReady]);

  return {
    isWebView,
    isWebViewReady,
    openNativeDialog,
    closeNativeDialog,
    onWebViewRendered,
    isWebViewDialogClosing,
    webViewPlatform,
    supportsDarkMode,
    theme,
    openNativeNotificationSettings,
    supportsLocalAuth,
    authenticateUser,
    authResult,
  };
};

export const useRaiseWebViewRenderedEvent = (area: string, isLoading: boolean | undefined, hasError?: boolean | undefined) => {
  const { isWebView, onWebViewRendered } = useWebView();

  useEffect(() => {
    if (isWebView && (!isLoading || hasError)) {
      // Notify a native app so it can remove a native shimmer if needed
      onWebViewRendered(area);
    }
  }, [area, hasError, isLoading, isWebView, onWebViewRendered]);
};

export const useWebViewDialogContext = () => {
  const { isWebView, webViewPlatform } = useWebView();

  let path: string = window.location.pathname || '';
  if (path.endsWith('/')) {
    path = path.slice(0, -1); // trim '/'
  }

  const isOnboardingRoute: boolean =
    localeIgnoreCaseEquals(path, identityOnboardingPath) || localeIgnoreCaseEquals(path, creditMonitoringOnboardingPath);

  const query = new URLSearchParams(useLocation().search);
  const openDialogSourceQueryParam = query.get(openDialogSourceQueryParamName);
  const isDialogOpenFromWebView = localeIgnoreCaseEquals(openDialogSourceQueryParam, 'webview');
  const isMobile = isWebView && isMobilePlatform(webViewPlatform);

  // Show close button for mobile webview full page dialogs (i.e. onboarding flow from mobile app dashboard)
  // Note that we don't show the close button for the onboarding flow from the web app as all native dialogs
  // from web app have a native close button already
  const showWebViewDialogCloseButton = isWebView && isOnboardingRoute && isMobile && !isDialogOpenFromWebView;

  return { showWebViewDialogCloseButton };
};
